// EnumOptions.js

export const ENUM_OPTION_SORT_MODE_MANUAL = 'manual';
export const ENUM_OPTION_SORT_MODE_LABEL_ASC = 'label_asc';
export const ENUM_OPTION_SORT_MODE_LABEL_DESC = 'label_desc';
export const ENUM_OPTION_SORT_MODE_INHERIT = 'inherit';

export const ENUM_OPTION_SORT_MODES = Object.freeze([
  ENUM_OPTION_SORT_MODE_MANUAL,
  ENUM_OPTION_SORT_MODE_LABEL_ASC,
  ENUM_OPTION_SORT_MODE_LABEL_DESC,
]);

export const ENUM_OPTION_VIEW_SORT_MODES = Object.freeze([
  ENUM_OPTION_SORT_MODE_INHERIT,
  ...ENUM_OPTION_SORT_MODES,
]);

/**
 * Normalize enum options into canonical objects with value + translations.
 * Accepts legacy string arrays and merges duplicate entries.
 *
 * @param {object[]} options
 * @returns {Array<{ id: string, value: string, translations: Record<string, { label: string }>, color?: string, iconKey?: string }>}
 */
export function normalizeEnumOptions(options) {
  if (!Array.isArray(options)) return [];

  const normalized = [];
  const seen = new Map();

  options.forEach((opt) => {
    if (opt == null) return;

    if (typeof opt === 'string') {
      const rawValue = opt.trim();
      if (!rawValue) return;
      const merged = mergeOption(seen.get(rawValue), {
        id: buildLegacyEnumOptionId(rawValue),
        value: rawValue,
        translations: {
          en: { label: rawValue },
        },
        color: undefined,
        iconKey: undefined,
      });
      seen.set(rawValue, merged);
      return;
    }

    if (typeof opt === 'object') {
      const valueCandidate = opt.value ?? opt.label ?? '';
      const value = typeof valueCandidate === 'string'
        ? valueCandidate.trim()
        : String(valueCandidate);
      if (!value) return;

      const translations = normalizeOptionTranslations(opt.translations, opt.label, value);
      const color = typeof opt.color === 'string' && opt.color.trim().length
        ? opt.color.trim()
        : undefined;
      const iconKey = sanitizeOptionIconKey(opt.iconKey);
      const id = sanitizeOptionId(opt.id) || buildLegacyEnumOptionId(value);
      const merged = mergeOption(seen.get(value), {
        id,
        value,
        translations,
        color,
        iconKey,
      });
      seen.set(value, merged);
      return;
    }
  });

  seen.forEach((opt) => {
    if (opt?.value) normalized.push(opt);
  });

  return normalized;
}

/**
 * Normalize enum option values into a unique, trimmed string list.
 * This is intentionally string-only (no translations).
 *
 * @param {Array<string|object>} options
 * @returns {string[]}
 */
export function normalizeEnumOptionValues(options) {
  if (!Array.isArray(options)) return [];

  const seen = new Set();
  const out = [];

  options.forEach((opt) => {
    if (opt == null) return;
    if (typeof opt === 'string') {
      const value = opt.trim();
      if (!value || seen.has(value)) return;
      seen.add(value);
      out.push(value);
      return;
    }
    if (typeof opt === 'object') {
      const candidate = opt.value ?? opt.label ?? '';
      const value = typeof candidate === 'string' ? candidate.trim() : String(candidate);
      if (!value || seen.has(value)) return;
      seen.add(value);
      out.push(value);
    }
  });

  return out;
}

export function normalizeEnumOptionsSortMode(sortMode) {
  const normalized = String(sortMode || '').trim().toLowerCase();
  if (ENUM_OPTION_SORT_MODES.includes(normalized)) return normalized;
  return ENUM_OPTION_SORT_MODE_MANUAL;
}

export function normalizeEnumOptionsViewSortMode(sortMode) {
  const normalized = String(sortMode || '').trim().toLowerCase();
  if (ENUM_OPTION_VIEW_SORT_MODES.includes(normalized)) return normalized;
  return ENUM_OPTION_SORT_MODE_INHERIT;
}

export function sortEnumOptions(options, { sortMode = ENUM_OPTION_SORT_MODE_MANUAL, language = 'en' } = {}) {
  const normalizedOptions = normalizeEnumOptions(options);
  const normalizedSortMode = normalizeEnumOptionsSortMode(sortMode);

  if (normalizedSortMode === ENUM_OPTION_SORT_MODE_MANUAL) {
    return normalizedOptions;
  }

  const direction = normalizedSortMode === ENUM_OPTION_SORT_MODE_LABEL_DESC ? -1 : 1;
  return [...normalizedOptions].sort((left, right) => {
    const leftLabel = String(getOptionLabel(left, language) || left?.value || '');
    const rightLabel = String(getOptionLabel(right, language) || right?.value || '');

    const byLabel = leftLabel.localeCompare(rightLabel, undefined, { sensitivity: 'base' });
    if (byLabel !== 0) return byLabel * direction;

    const leftValue = String(left?.value || '');
    const rightValue = String(right?.value || '');
    return leftValue.localeCompare(rightValue, undefined, { sensitivity: 'base' }) * direction;
  });
}

export function resolveEnumOptionsForField(fieldDef, language = 'en', config = {}) {
  const options = fieldDef?.options || [];
  const fieldSortMode = normalizeEnumOptionsSortMode(fieldDef?.enumOptionsSort);
  const viewSortMode = normalizeEnumOptionsViewSortMode(config?.sortModeOverride);
  const effectiveSortMode = viewSortMode === ENUM_OPTION_SORT_MODE_INHERIT
    ? fieldSortMode
    : viewSortMode;

  const sorted = sortEnumOptions(options, { sortMode: effectiveSortMode, language });

  const hiddenOptionIds = Array.isArray(config?.hiddenOptionIds)
    ? [...new Set(config.hiddenOptionIds
      .map((value) => String(value || '').trim())
      .filter(Boolean))]
    : [];

  const hiddenSet = new Set(hiddenOptionIds);
  const visible = hiddenOptionIds.length
    ? sorted.filter((option) => !hiddenSet.has(String(option?.id || '').trim()))
    : sorted;

  const optionOrderIds = Array.isArray(config?.optionOrderIds)
    ? [...new Set(config.optionOrderIds
      .map((value) => String(value || '').trim())
      .filter(Boolean))]
    : [];

  if (!optionOrderIds.length) return visible;

  const usedIds = new Set();
  const optionById = new Map();
  visible.forEach((option) => {
    const optionId = String(option?.id || '').trim();
    if (!optionId) return;
    optionById.set(optionId, option);
  });

  const ordered = [];
  optionOrderIds.forEach((optionId) => {
    const option = optionById.get(optionId);
    if (!option) return;
    usedIds.add(optionId);
    ordered.push(option);
  });

  const remainder = visible.filter((option) => {
    const optionId = String(option?.id || '').trim();
    return !optionId || !usedIds.has(optionId);
  });

  return [...ordered, ...remainder];
}

export function buildEnumOptionValueRankMap(options) {
  const ranks = new Map();

  normalizeEnumOptions(options).forEach((option, index) => {
    const value = String(option?.value || '').trim();
    if (!value || ranks.has(value)) return;
    ranks.set(value, index);
  });

  return ranks;
}

/**
 * Create a new unique enum option id for newly added options.
 */
export function createEnumOptionId() {
  const randomPart = Math.random().toString(36).slice(2, 10);
  const timePart = Date.now().toString(36);
  return `opt_${timePart}${randomPart}`;
}

function sanitizeOptionId(rawId) {
  if (rawId == null) return '';
  const candidate = String(rawId).trim();
  return candidate || '';
}

function sanitizeOptionIconKey(rawIconKey) {
  if (rawIconKey == null) return undefined;
  const candidate = String(rawIconKey).trim();
  return candidate || undefined;
}

function buildLegacyEnumOptionId(value) {
  const normalized = String(value || '').trim();
  if (!normalized) return createEnumOptionId();
  return `legacy_${hashString(normalized)}`;
}

function hashString(input) {
  let hash = 2166136261;
  for (let i = 0; i < input.length; i += 1) {
    hash ^= input.charCodeAt(i);
    hash = Math.imul(hash, 16777619);
  }
  return (hash >>> 0).toString(36);
}

/**
 * Build a normalized translations map for an option.
 */
function normalizeOptionTranslations(translations, fallbackLabel, fallbackValue) {
  const out = {};

  if (translations && typeof translations === 'object') {
    Object.entries(translations).forEach(([lang, data]) => {
      if (!lang) return;
      let label;
      if (data && typeof data === 'object') {
        label = typeof data.label === 'string' ? data.label : undefined;
      } else if (typeof data === 'string') {
        label = data;
      }
      if (typeof label === 'string') {
        out[lang] = { label: label };
      }
    });
  }

  const fallback = fallbackLabel && typeof fallbackLabel === 'string' && fallbackLabel.trim().length
    ? fallbackLabel
    : fallbackValue;

  if (!out.en || typeof out.en.label !== 'string' || out.en.label.trim().length === 0) {
    const fallbackStr = fallback != null ? String(fallback) : '';
    out.en = { label: fallbackStr };
  }

  return out;
}

/**
 * Merge two option objects, preferring existing translations.
 */
function mergeOption(existing, next) {
  if (!existing) return next;
  if (!next) return existing;

  const translations = { ...existing.translations };
  Object.entries(next.translations || {}).forEach(([lang, data]) => {
    if (!data) return;
    if (!translations[lang] || !translations[lang].label) {
      translations[lang] = { label: data.label };
    }
  });

  return {
    id: existing.id || next.id || createEnumOptionId(),
    value: existing.value,
    translations,
    color: existing.color || next.color,
    iconKey: existing.iconKey || next.iconKey,
  };
}

/**
 * Return a list of option values in order.
 */
export function enumOptionValues(options) {
  return normalizeEnumOptions(options).map((opt) => opt.value);
}

/**
 * Find an option object (string-safe) by value.
 */
export function findEnumOption(options, value) {
  if (!Array.isArray(options)) return null;
  const target = value != null ? String(value) : '';
  for (const opt of options) {
    if (opt == null) continue;
    if (typeof opt === 'string' && opt === target) {
      return {
        id: buildLegacyEnumOptionId(opt),
        value: opt,
        translations: { en: { label: opt } },
        color: undefined,
        iconKey: undefined,
      };
    }
    if (typeof opt === 'object') {
      const optValue = opt.value != null ? String(opt.value) : undefined;
      if (optValue === target) {
        const translations = normalizeOptionTranslations(opt.translations, opt.label, optValue);
        const color = typeof opt.color === 'string' && opt.color.trim().length
          ? opt.color.trim()
          : undefined;
        const iconKey = sanitizeOptionIconKey(opt.iconKey);
        return {
          id: sanitizeOptionId(opt.id) || buildLegacyEnumOptionId(optValue),
          value: optValue,
          translations,
          color,
          iconKey,
        };
      }
    }
  }
  return null;
}

/**
 * Resolve a label for an option value in the requested language.
 */
export function getEnumOptionLabel(options, value, language = 'en') {
  const option = findEnumOption(options, value);
  if (!option) return value != null ? String(value) : '';
  return getOptionLabel(option, language);
}

/**
 * Resolve a color for an option value.
 */
export function getEnumOptionColor(options, value) {
  const option = findEnumOption(options, value);
  return option?.color || '';
}

/**
 * Resolve an icon key for an option value.
 */
export function getEnumOptionIconKey(options, value) {
  const option = findEnumOption(options, value);
  return option?.iconKey || '';
}

/**
 * Resolve a label directly from an option object.
 */
export function getOptionLabel(option, language = 'en') {
  if (!option) return '';
  if (typeof option === 'string') return option;

  const translations = option.translations || {};
  const preferred = translations[language]?.label;
  if (preferred && preferred.trim().length) return preferred;

  const english = translations.en?.label;
  if (english && english.trim().length) return english;

  if (typeof option.label === 'string' && option.label.trim().length) return option.label;
  if (option.value != null) return String(option.value);
  return '';
}

/**
 * Update or insert a translation for a given option value.
 */
// Translation helpers removed for enum options (string-only).
